import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { KeyRound, Copy, Check, RefreshCw, X, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";

/**
 * PasswordResetTicketsPanel — admin-only mini-dashboard that lists pending
 * password reset tickets (filed by partners via /forgot-password) and lets
 * the admin resolve each one in a click. Resolving generates a fresh
 * temporary password that is returned ONCE and displayed in a modal copy
 * box — the admin then shares it out-of-band (WhatsApp / phone). After
 * dismissing the modal the value disappears forever.
 *
 * Mounted as a collapsible panel above the main admin tabs so admins see
 * pending tickets without changing tab.
 */
export default function PasswordResetTicketsPanel() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actingOn, setActingOn] = useState(null);
  const [resolved, setResolved] = useState(null); // {email, temp_pw}
  const [copied, setCopied] = useState(false);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/password-reset", { params: { status: "pending", limit: 50 } });
      setItems(r.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de charger les tickets.");
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on first open so admins don't waste a click.
  useEffect(() => { if (open) fetchTickets(); }, [open]);

  const resolve = async (email) => {
    if (!window.confirm(`Générer un mot de passe temporaire pour ${email} ?\nLa valeur ne sera affichée qu'une seule fois — pensez à la communiquer immédiatement au partenaire.`)) return;
    setActingOn(email);
    try {
      const r = await api.post(`/admin/password-reset/${encodeURIComponent(email)}/resolve`);
      setResolved({ email: r.data?.email, temp_pw: r.data?.temporary_password });
      await fetchTickets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la résolution.");
    } finally {
      setActingOn(null);
    }
  };

  const reject = async (email) => {
    if (!window.confirm(`Rejeter le ticket pour ${email} ?\n(Demande suspecte / déjà traitée hors-ligne.)`)) return;
    setActingOn(email);
    try {
      await api.post(`/admin/password-reset/${encodeURIComponent(email)}/reject`);
      toast.success(`Ticket de ${email} rejeté.`);
      await fetchTickets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du rejet.");
    } finally {
      setActingOn(null);
    }
  };

  const copyTempPw = async () => {
    if (!resolved?.temp_pw) return;
    try {
      await navigator.clipboard.writeText(resolved.temp_pw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Impossible de copier dans le presse-papiers.");
    }
  };

  return (
    <div className="mb-4 border border-zinc-300 bg-white rounded-sm overflow-hidden" data-testid="password-reset-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left bg-gradient-to-r from-[#D42B1E]/[0.04] to-transparent hover:from-[#D42B1E]/[0.08] transition-colors"
        data-testid="password-reset-panel-toggle"
      >
        <div className="flex items-center gap-2.5">
          <KeyRound className="h-4 w-4 text-[#D42B1E]" />
          <span className="text-sm font-semibold text-zinc-900">Demandes de réinitialisation de mot de passe</span>
          {items.length > 0 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-mono uppercase">
              {items.length} en attente
            </span>
          )}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
      </button>

      {open && (
        <div className="border-t border-zinc-200">
          <div className="px-4 py-2 border-b border-zinc-100 bg-zinc-50/40 flex items-center justify-between gap-2">
            <p className="text-[11px] text-zinc-500">
              Une fois résolu, le mot de passe temporaire est affiché <strong>une seule fois</strong> — communiquez-le au partenaire via WhatsApp ou téléphone.
            </p>
            <Button size="sm" variant="ghost" onClick={fetchTickets} disabled={loading} className="rounded-sm h-7 px-2" data-testid="password-reset-refresh">
              <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>

          {items.length === 0 ? (
            <div className="px-4 py-6 text-center text-[12px] text-zinc-500">
              {loading ? "Chargement…" : "Aucune demande en attente."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/50 text-[10px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-4 py-2 text-left">Email</th>
                  <th className="px-4 py-2 text-left">Raison</th>
                  <th className="px-4 py-2 text-left">Reçue</th>
                  <th className="px-4 py-2 text-center">Compte</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t, idx) => (
                  <tr key={`${t.email}-${idx}`} className="border-t border-zinc-100" data-testid={`password-reset-row-${t.email}`}>
                    <td className="px-4 py-2 font-mono text-[11.5px] text-zinc-900">{t.email}</td>
                    <td className="px-4 py-2 text-[12px] text-zinc-600 max-w-md truncate" title={t.reason || ""}>
                      {t.reason || <span className="text-zinc-400 italic">aucune</span>}
                    </td>
                    <td className="px-4 py-2 text-[11px] text-zinc-500 whitespace-nowrap tabular-nums">
                      {t.submitted_at ? new Date(t.submitted_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {t.user_exists ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">
                          existe
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-500">
                          <AlertTriangle className="h-2.5 w-2.5" /> inconnu
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => reject(t.email)}
                          disabled={actingOn === t.email}
                          className="rounded-sm h-7 px-2 text-zinc-600 hover:text-rose-700"
                          data-testid={`password-reset-reject-${t.email}`}
                        >
                          <X className="h-3 w-3 mr-1" /> Rejeter
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => resolve(t.email)}
                          disabled={actingOn === t.email || !t.user_exists}
                          className="rounded-sm h-7 px-2 bg-[#D42B1E] hover:bg-[#B32218]"
                          title={t.user_exists ? "Générer un mot de passe temporaire" : "Aucun compte associé"}
                          data-testid={`password-reset-resolve-${t.email}`}
                        >
                          <KeyRound className="h-3 w-3 mr-1" /> Réinitialiser
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modal — one-time temporary password reveal */}
      {resolved && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="password-reset-reveal-modal"
        >
          <div className="bg-white max-w-md w-full rounded-sm border border-zinc-300 shadow-2xl">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-emerald-600" />
              <p className="text-sm font-semibold text-zinc-900">Mot de passe temporaire généré</p>
            </div>
            <div className="px-5 py-5 space-y-3">
              <p className="text-[12.5px] text-zinc-700">
                Pour <span className="font-mono text-zinc-900">{resolved.email}</span> — communiquez cette valeur immédiatement au partenaire via WhatsApp ou téléphone.
                <strong className="block mt-1 text-rose-700">Elle ne sera plus jamais affichée.</strong>
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-zinc-100 border border-zinc-300 rounded-sm font-mono text-[14px] text-zinc-900 tracking-wider select-all">
                  {resolved.temp_pw}
                </code>
                <Button
                  type="button"
                  onClick={copyTempPw}
                  size="sm"
                  className="rounded-sm bg-[#D42B1E] hover:bg-[#B32218]"
                  data-testid="password-reset-copy"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? " Copié" : " Copier"}
                </Button>
              </div>
              <p className="text-[11px] text-zinc-500">
                Recommandez au partenaire de modifier ce mot de passe dès sa première connexion depuis son profil.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50/50 flex justify-end">
              <Button
                type="button"
                onClick={() => { setResolved(null); setCopied(false); }}
                variant="outline"
                size="sm"
                className="rounded-sm"
                data-testid="password-reset-close-modal"
              >
                J'ai noté le mot de passe — fermer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
